import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Box, Paper, Typography } from "@mui/material";
import FormInput from "../Common/FormInputs/MuiTextField";
import { selectMenuProps } from "../../utils/selectMenuProps";
import { notificationTemplateService } from "../../services/notificationTemplate.service";
import { clonePlain } from "./notificationFormUtils";
import { getNotificationPermission } from "./notificationConstant";
import {
  emailCompactFormControlSx,
  emailFieldRowSx,
  emailFocusOutlineSx,
  emailSectionCardSx,
} from "./emailFormStyles";
import { useTheme } from "@mui/material/styles";
import { useDesignTokens } from "../../theme/useDesignTokens";

const LoadTemplate = ({
  notificationType,
  onLoad,
  disabled = false,
  embedded = false,
  userRoles = [],
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const focusOutlineSx = emailFocusOutlineSx(primaryColor);
  const controlSx = emailCompactFormControlSx(theme);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");

  const canLoadTemplates = getNotificationPermission(userRoles, "notificationTemplates");

  useEffect(() => {
    let cancelled = false;
    const loadTemplates = async () => {
      if (!canLoadTemplates) {
        setTemplates([]);
        setError("");
        setSelectedId("");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      setSelectedId("");
      const result = await notificationTemplateService.getTemplates({
        channel: notificationType,
        pageIndex: 0,
        pageSize: 100,
      });
      if (cancelled) return;
      setLoading(false);
      if (!result.success) {
        setTemplates([]);
        setError(result.message || "Failed to load templates");
        return;
      }
      setTemplates(result.data || []);
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [notificationType, canLoadTemplates]);

  const options = useMemo(
    () => [
      { value: "", label: "Select template" },
      ...templates.map((template) => ({
        value: template.id || template.templateId,
        label: template.templateName,
      })),
    ],
    [templates]
  );

  const handleSelectTemplate = async (templateId) => {
    if (!canLoadTemplates) return;
    setSelectedId(templateId);
    setError("");
    if (!templateId) return;
    setLoading(true);
    const result = await notificationTemplateService.getTemplateById(templateId);
    setLoading(false);
    if (!result.success || !result.data) {
      setError(result.message || "Failed to load template");
      return;
    }
    onLoad?.(clonePlain(result.data));
  };

  if (!canLoadTemplates) {
    return null;
  }

  const statusMessage = loading
    ? "Loading templates..."
    : error
      ? error
      : !templates.length
        ? "No templates available."
        : "";

  const dropdown = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: { xs: "wrap", md: "nowrap" },
        minWidth: 0,
        width: "100%",
      }}
    >
      <Box sx={{ ...controlSx, flex: "0 0 auto" }}>
        <FormInput
          type="select"
          size="small"
          fullWidth
          value={selectedId}
          onChange={(e) => handleSelectTemplate(e.target.value)}
          options={options}
          displayEmpty
          renderValue={(value) => {
            if (!value) return "Select template";
            const match = templates.find((item) => (item.id || item.templateId) === value);
            return match?.templateName || value;
          }}
          MenuProps={selectMenuProps}
          sx={focusOutlineSx}
          disabled={disabled || loading || !templates.length}
        />
      </Box>
      {statusMessage ? (
        <Typography
          variant="caption"
          color={error ? "error.main" : "text.secondary"}
          sx={{
            flex: "1 1 180px",
            minWidth: 0,
            lineHeight: 1.4,
          }}
        >
          {statusMessage}
        </Typography>
      ) : null}
    </Box>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          ...emailFieldRowSx,
          gridTemplateColumns: { xs: "1fr", md: "200px minmax(0, 1fr)" },
          alignItems: { xs: "start", md: "center" },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 400 }}>
          Template
        </Typography>
        {dropdown}
      </Box>
    );
  }

  return (
    <>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
        Load Template
      </Typography>
      <Paper sx={emailSectionCardSx}>
        {dropdown}
      </Paper>
    </>
  );
};

export default connect((state) => ({ userRoles: state.auth?.roles || [] }))(LoadTemplate);
